import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, Phone, X } from "lucide-react";
import Logo from "@/components/Logo";
import { CATEGORIES } from "@/data/products";
import { useSectionNav } from "@/hooks/useSectionNav";
import { useLang, EN_CATEGORY_LABELS } from "@/i18n";

const LINKS_TOP = [
    { id: "kopfstationen", de: "Kopfstationen", en: "Headends", href: "#kopfstationen" },
    { id: "leistungen", de: "Leistungen", en: "Services", href: "#leistungen" },
];

const DL_LINKS = [
    { id: "uebersicht", de: "Übersicht", en: "Overview", to: "/downloads" },
    { id: "software", de: "Software", en: "Software", to: "/downloads/software" },
    { id: "bedienungsanleitungen", de: "Bedienungsanleitungen", en: "Manuals", to: "/downloads/bedienungsanleitungen" },
];

const LINKS_REST = [
    { id: "48h-service", de: "48h Service", en: "48h Service", href: "#reparatur" },
    { id: "haendler", de: "Händler", en: "Dealers", href: "#haendler" },
    { id: "kontakt", de: "Kontakt", en: "Contact", href: "#kontakt" },
];

const testid = (prefix, label) =>
    `${prefix}-${label.toLowerCase().replace(/\s+/g, "-")}`;

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [prodOpen, setProdOpen] = useState(false);
    const [dlOpen, setDlOpen] = useState(false);
    const location = useLocation();
    const go = useSectionNav();
    const { lang, setLang, t } = useLang();

    useEffect(() => {
        const on = () => setScrolled(window.scrollY > 24);
        on();
        window.addEventListener("scroll", on, { passive: true });
        return () => window.removeEventListener("scroll", on);
    }, []);

    const sectionLink = (l, mobile = false) => (
        <a
            key={l.href}
            href={l.href}
            onClick={(e) => {
                e.preventDefault();
                if (mobile) setOpen(false);
                go(l.href);
            }}
            data-testid={testid(mobile ? "mobile-nav-link" : "nav-link", l.id)}
            className={
                mobile
                    ? "rounded-lg px-3 py-3 text-base font-medium text-slate-700 hover:bg-slate-100 hover:text-red-700 transition-colors"
                    : "text-sm font-medium text-slate-600 hover:text-red-700 transition-colors"
            }
        >
            {t(l.de, l.en)}
        </a>
    );

    const catLabel = (c) => t(c.label, EN_CATEGORY_LABELS[c.slug] || c.label);

    const productsLink = (mobile = false) =>
        mobile ? (
            <div key="produkte-mobile">
                <button
                    onClick={() => setProdOpen(!prodOpen)}
                    data-testid="mobile-nav-produkte-toggle"
                    className="w-full flex items-center justify-between rounded-lg px-3 py-3 text-base font-medium text-slate-700 hover:bg-slate-100 transition-colors"
                >
                    {t("Produkte", "Products")}
                    <ChevronDown className={`h-4 w-4 transition-transform ${prodOpen ? "rotate-180" : ""}`} />
                </button>
                {prodOpen && (
                    <div className="pl-4 pb-2 flex flex-col" data-testid="mobile-nav-produkte-submenu">
                        <Link
                            to="/produkte"
                            onClick={() => setOpen(false)}
                            data-testid="mobile-nav-sub-uebersicht"
                            className="rounded-lg px-3 py-2.5 text-sm font-semibold text-red-700 hover:bg-red-50 transition-colors"
                        >
                            {t("Alle Produkte im Überblick", "All products at a glance")}
                        </Link>
                        {CATEGORIES.map((c) => (
                            <Link
                                key={c.slug}
                                to={`/produkte/${c.slug}`}
                                onClick={() => setOpen(false)}
                                data-testid={`mobile-nav-sub-${c.slug}`}
                                className="rounded-lg px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-100 hover:text-red-700 transition-colors"
                            >
                                {catLabel(c)}
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        ) : (
            <div key="produkte-desktop" className="relative group" data-testid="nav-produkte-dropdown">
                <Link
                    to="/produkte"
                    data-testid="nav-link-produkte"
                    className="text-sm font-medium text-slate-600 hover:text-red-700 transition-colors flex items-center gap-1"
                >
                    {t("Produkte", "Products")}
                    <ChevronDown className="h-3.5 w-3.5 transition-transform duration-200 group-hover:rotate-180" />
                </Link>
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <div className="w-72 rounded-xl bg-white border border-slate-200 shadow-lg py-2">
                        <Link
                            to="/produkte"
                            data-testid="nav-sub-uebersicht"
                            className="block px-4 py-2.5 text-sm font-semibold text-red-700 hover:bg-red-50 transition-colors"
                        >
                            {t("Alle Produkte im Überblick", "All products at a glance")}
                        </Link>
                        {CATEGORIES.map((c) => (
                            <Link
                                key={c.slug}
                                to={`/produkte/${c.slug}`}
                                data-testid={`nav-sub-${c.slug}`}
                                className="block px-4 py-2 text-sm text-slate-700 hover:bg-red-50 hover:text-red-700 transition-colors"
                            >
                                {catLabel(c)}
                            </Link>
                        ))}
                    </div>
                </div>
            </div>
        );

    const downloadLink = (mobile = false) =>
        mobile ? (
            <div key="download-mobile">
                <button
                    onClick={() => setDlOpen(!dlOpen)}
                    data-testid="mobile-nav-download-toggle"
                    className="w-full flex items-center justify-between rounded-lg px-3 py-3 text-base font-medium text-slate-700 hover:bg-slate-100 transition-colors"
                >
                    {t("Download", "Downloads")}
                    <ChevronDown className={`h-4 w-4 transition-transform ${dlOpen ? "rotate-180" : ""}`} />
                </button>
                {dlOpen && (
                    <div className="pl-4 pb-2 flex flex-col" data-testid="mobile-nav-download-submenu">
                        {DL_LINKS.map((d) => (
                            <Link
                                key={d.to}
                                to={d.to}
                                onClick={() => setOpen(false)}
                                data-testid={`mobile-nav-dl-${d.id}`}
                                className="rounded-lg px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-100 hover:text-red-700 transition-colors"
                            >
                                {t(d.de, d.en)}
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        ) : (
            <div key="download-desktop" className="relative group" data-testid="nav-download-dropdown">
                <Link
                    to="/downloads"
                    data-testid="nav-link-download"
                    className="text-sm font-medium text-slate-600 hover:text-red-700 transition-colors flex items-center gap-1"
                >
                    {t("Download", "Downloads")}
                    <ChevronDown className="h-3.5 w-3.5 transition-transform duration-200 group-hover:rotate-180" />
                </Link>
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <div className="w-64 rounded-xl bg-white border border-slate-200 shadow-lg py-2">
                        {DL_LINKS.map((d) => (
                            <Link
                                key={d.to}
                                to={d.to}
                                data-testid={`nav-dl-${d.id}`}
                                className="block px-4 py-2 text-sm text-slate-700 hover:bg-red-50 hover:text-red-700 transition-colors"
                            >
                                {t(d.de, d.en)}
                            </Link>
                        ))}
                    </div>
                </div>
            </div>
        );

    return (
        <header
            data-testid="navbar"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                scrolled || open
                    ? "bg-white/85 backdrop-blur-md border-b border-slate-200/80 shadow-sm"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 h-[84px] flex items-center justify-between">
                <Logo />
                <nav className="hidden lg:flex items-center gap-7">
                    {LINKS_TOP.map((l) => sectionLink(l))}
                    {productsLink(false)}
                    {downloadLink(false)}
                    {LINKS_REST.map((l) => sectionLink(l))}
                </nav>
                <div className="flex items-center gap-3">
                    <div className="flex items-center rounded-full border border-slate-200 bg-white/80 overflow-hidden" data-testid="lang-switcher">
                        {["de", "en"].map((code) => (
                            <button
                                key={code}
                                onClick={() => setLang(code)}
                                data-testid={`lang-switch-${code}`}
                                aria-pressed={lang === code}
                                className={`px-2.5 py-1.5 text-xs font-bold uppercase tracking-wide transition-colors ${
                                    lang === code ? "bg-red-700 text-white" : "text-slate-500 hover:text-red-700"
                                }`}
                            >
                                {code}
                            </button>
                        ))}
                    </div>
                    <a
                        href="tel:+49919594040"
                        data-testid="navbar-phone-cta"
                        className="hidden sm:inline-flex items-center gap-2 rounded-full bg-red-700 hover:bg-red-800 text-white text-sm font-semibold px-5 py-2.5 transition-colors"
                    >
                        <Phone className="h-4 w-4" />
                        +49 (0) 9195 / 9404-0
                    </a>
                    <button
                        onClick={() => setOpen(!open)}
                        data-testid="mobile-menu-toggle"
                        aria-label={t("Menü öffnen", "Open menu")}
                        className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border border-slate-200 bg-white/80 text-slate-700"
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>
            <AnimatePresence>
                {open && (
                    <motion.nav
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-md border-b border-slate-200/80"
                        data-testid="mobile-menu"
                    >
                        <div className="px-4 sm:px-6 py-4 flex flex-col gap-1">
                            {LINKS_TOP.map((l) => sectionLink(l, true))}
                            {productsLink(true)}
                            {downloadLink(true)}
                            {LINKS_REST.map((l) => sectionLink(l, true))}
                            <a
                                href="tel:+49919594040"
                                data-testid="mobile-menu-phone-cta"
                                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-red-700 text-white text-sm font-semibold px-5 py-3"
                            >
                                <Phone className="h-4 w-4" />
                                +49 (0) 9195 / 9404-0
                            </a>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
}
